import { useState } from "react"
import { errorToast, toast } from "@/v2-ui/toast"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import FeatureCreateIdentity from "@/v2-backoffice-client/feature/create/FeatureCreateIdentity"
import FeatureConfigurationEditor from "@/v2-backoffice-client/feature/configuration/FeatureConfigurationEditor"
import { FEATURE_CODE_PATTERN } from "@/v2-backoffice-client/feature/feature.constants"
import {
  getNavLayerEditorModelPath,
  NAV_LAYER_CREATE_CONFIGURATION,
} from "@/v2-backoffice-client/navlayer/update/navlayer.update.schema"
import { useNavLayerCreateMutation } from "@/v2-backoffice-client/navlayer/create/navlayer.create.mutation"
import {
  NAV_LAYER_FEATURE_CODE_PREFIX,
  NAV_LAYER_QUERY_NAME,
} from "@/v2-backoffice-client/navlayer/navlayer.constants"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import type { FeatureCreateIdentityValue } from "@/v2-backoffice-client/feature/create/feature.create.types"

const EMPTY_IDENTITY: FeatureCreateIdentityValue = {
  featureCode: NAV_LAYER_FEATURE_CODE_PREFIX,
  name: "",
  description: "",
}

/**
 * The nav layer create flow: identity plus the same schema-assisted
 * configuration editor the sheet edits with, seeded with every key the
 * runtime's parser requires.
 *
 * The row lands disabled, so a nav layer cannot start answering map clicks
 * before someone has checked which countries it should serve.
 */
function NavLayerCreateSheet() {
  const opener = useAppViewSheetOpener()

  const [identity, setIdentity] = useState(EMPTY_IDENTITY)

  const [createNavLayer, { loading: isCreating, error: createError }] =
    useNavLayerCreateMutation({
      refetchQueries: [NAV_LAYER_QUERY_NAME, LAYER_COUNTS_QUERY_NAME],
    })
  errorToast(createError, { title: "Failed to create the nav layer" })

  async function create(configuration: unknown) {
    const featureCode = identity.featureCode.trim()
    if (!FEATURE_CODE_PATTERN.test(featureCode)) {
      toast({
        title: "Fix the feature code first",
        description:
          "Letters, digits and underscores only, starting with a letter.",
        variant: "destructive",
      })
      return
    }
    if (!identity.name.trim()) {
      toast({
        title: "Name the nav layer first",
        description: "The en-GB name is what the rest of the backoffice lists.",
        variant: "destructive",
      })
      return
    }

    const result = await createNavLayer({
      variables: {
        input: {
          FeatureCode: featureCode,
          Name: identity.name.trim(),
          Description: identity.description.trim() || undefined,
          Configuration: configuration,
        },
      },
    })

    const created = result.data?.createNavLayer
    if (!created) return

    toast({
      title: "Nav layer created.",
      description:
        "It starts disabled — no map click resolves against it until it goes live.",
    })
    opener.open("navLayer", created.FeatureCode)
  }

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title>New nav layer</ViewSheet.Title>
      </ViewSheet.Header>

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FeatureCreateIdentity
          idPrefix="navlayer-create"
          codePlaceholder={`${NAV_LAYER_FEATURE_CODE_PREFIX}…`}
          subject="nav layer"
          identity={identity}
          onIdentityChange={setIdentity}
        />

        {/* The editor owns the draft text — the create payload comes from
            its onSave, so nothing here has to mirror every keystroke. */}
        <FeatureConfigurationEditor
          className="min-h-[420px]"
          modelPath={`create/${getNavLayerEditorModelPath()}`}
          value={NAV_LAYER_CREATE_CONFIGURATION}
          isSaving={isCreating}
          saveLabel="Create nav layer"
          canSaveClean
          onSave={(configuration) => void create(configuration)}
        />
      </div>
    </>
  )
}

export default NavLayerCreateSheet
